import React, { useState, useMemo } from 'react';
import { View, LogBox, Dimensions, Modal, ScrollView } from 'react-native';
import { interpolateGnBu, interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import { Loader } from '../../Common/Loader';
import ToastMessage from '../common/controls/Toast';
import {
  filterData,
  handleFilterReset,
  handleExportClick,
  constructData,
  EnableExport,
  CommonStyles,
  GetConsolidatedLog,
  FilterChoices
} from './ConsolidatedController';
import { FilterIcon, ExportIcon, FilterBox, SelectedFilters } from './ReUsableComps';
import formCustomStyles from '../common/forms/formStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import { colorType5, logStyles } from '../LogControllers/LogStyles';
import LogKpiRow from '../LogControllers/LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import processData from '../common/service/dataProcessor';

interface Props {
  siteId: string;
  logTitle: string;
  projectId: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

import { useQuery } from '@tanstack/react-query';

export default function ConsolImportedSoilLog(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);

  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['consolidated-log', props.logTitle, props.projectId],
    queryFn: async () => {
      const accessToken = await getAccessToken();
      const jResponse = await GetConsolidatedLog(props.logTitle, clientAPIURL, accessToken, props.projectId);
      return constructData(jResponse);
    },
  });

  if (isLoading) return <Loader />;

  return reportData && reportData.items && reportData.columns ? (
    <CreateSoilLogCharts
      LogData={reportData.items}
      LogColumns={reportData.columns}
      siteId={props.siteId}
      logTitle={props.logTitle}
      isDashboard={props.isDashboard}
      packageFilter={props.packageFilter}
    />
  ) : null;
}

interface CreateSoilLogChartsProps {
  LogData: any;
  LogColumns: any;
  siteId: string;
  logTitle: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

const CreateSoilLogCharts: React.FC<CreateSoilLogChartsProps> = ({ LogData, LogColumns, siteId, logTitle, isDashboard, packageFilter }) => {
  const [FilterVisible, setFilterVisible] = React.useState(false);
  const screenWidth = Dimensions.get('window').width;

  const ColumnsWithIDColumn = LogColumns && [
    ...LogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const updatedData = useMemo(() => {
    const raw = processData(logTitle, LogData);
    if (!packageFilter?.length) return raw;
    return raw.filter((item: any) => packageFilter.includes(item.packageName));
  }, [logTitle, LogData, packageFilter]);

  const [selectedYear, setSelectedYear] = useState<string>('ALL');
  const [selectedMonth, setSelectedMonth] = useState<string>('ALL');
  const [selectedPackage, setSelectedPackage] = useState<string>('ALL');
  const [toastVisibility, setToastVisibility] = useState<boolean>(false);
  const [toastText, setToastText] = useState<string>('');
  const [newData, setNewData] = useState<any[]>(updatedData);
  const [dateRangeFiltered, setDateRangeFiltered] = useState<any[]>(updatedData);

  const uniqueYearsArray = [...new Set(updatedData.map((item: any) => item.Year))];
  const yearFilterOptions = FilterChoices(uniqueYearsArray, 'Year');
  const uniqueMonthsArray = [...new Set(updatedData.map((item: any) => item.Month))];
  const monthFilterOptions = FilterChoices(uniqueMonthsArray, 'Month');
  const uniquePackagesArray = [...new Set(updatedData.map((item: any) => item.packageName))];
  const packageFilterOptions = FilterChoices(uniquePackagesArray, 'Package');

  function handleYearFilterSelect(val: string) {
    setSelectedYear(val);
    filterData(val, selectedMonth, selectedPackage, setToastVisibility, updatedData, setToastText, setNewData);
  }
  function handleMonthFilterSelect(val: string) {
    setSelectedMonth(val);
    filterData(selectedYear, val, selectedPackage, setToastVisibility, updatedData, setToastText, setNewData);
  }
  function handlePackageFilterSelect(val: string) {
    filterData(selectedYear, selectedMonth, val, setToastVisibility, updatedData, setToastText, setNewData);
    setSelectedPackage(val);
  }
  function handleToastDismiss() {
    setToastVisibility(false);
  }

  // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
  // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
  const chartData = useMemo(() => {
    const colorLine = scaleOrdinal(schemeCategory10);
    const uniquePeriod = [...new Set(dateRangeFiltered.map((item: any) => item.Period))] as string[];

    const pieAcc1: Record<string, number> = {};
    dateRangeFiltered.forEach((item: any) => {
      if (item.SoilType == null) return;
      pieAcc1[item.SoilType] = (pieAcc1[item.SoilType] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const bysoilTypePieData = Object.entries(pieAcc1).map(([name, value], index) => ({
      name, value: Math.round(value), color: colorType5(index.toString()),
    }));

    const pieAcc2: Record<string, number> = {};
    dateRangeFiltered.forEach((item: any) => {
      if (item.Use == null) return;
      pieAcc2[item.Use] = (pieAcc2[item.Use] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const byUsePieData = Object.entries(pieAcc2).map(([name, value], index) => ({
      name, value: Math.round(value), color: colorType5(index.toString()),
    }));

    const soilTypes = [...new Set(dateRangeFiltered.map((i: any) => i.SoilType).filter(Boolean))] as string[];
    const groupedBySoil: Record<string, number> = {};
    dateRangeFiltered.forEach((item: any) => {
      const k = `${item.Period}_${item.SoilType}`;
      groupedBySoil[k] = (groupedBySoil[k] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const lineData = {
      labels: uniquePeriod,
      datasets: soilTypes.map((soilType, index) => ({
        data: uniquePeriod.map(p => groupedBySoil[`${p}_${soilType}`] ?? 0),
        strokeWidth: 2,
        color: (opacity = 1) => colorLine(String(index)) + Math.round(opacity * 255).toString(16),
      })),
    };
    const legendData1 = soilTypes.map(name => ({ name }));

    const useTypes = [...new Set(dateRangeFiltered.map((i: any) => i.Use).filter(Boolean))] as string[];
    const groupedByUse: Record<string, number> = {};
    dateRangeFiltered.forEach((item: any) => {
      const k = `${item.Period}_${item.Use}`;
      groupedByUse[k] = (groupedByUse[k] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const lineData2 = {
      labels: uniquePeriod,
      datasets: useTypes.map((use, index) => ({
        data: uniquePeriod.map(p => groupedByUse[`${p}_${use}`] ?? 0),
        strokeWidth: 2,
        color: (opacity = 1) => colorLine(String(index + soilTypes.length)) + Math.round(opacity * 255).toString(16),
      })),
    };
    const legendData2 = useTypes.map(name => ({ name }));

    return { uniquePeriod, bysoilTypePieData, byUsePieData, lineData, lineData2, legendData1, legendData2 };
  }, [dateRangeFiltered]);

  const TotalCount = dateRangeFiltered.reduce((total: number, item: any) => total + (Number(item.QuantityTonne) || 0), 0);

  const kpiItems = [
    { label: 'Total Soil (Tonnes)', value: Math.round(TotalCount), color: '#00695C' },
    { label: 'Entries', value: dateRangeFiltered.length, color: '#4527A0' },
  ];

  return (
    <View style={logStyles.logRoot}>
      <View style={CommonStyles.ribbonIconContainer}>
        <View style={CommonStyles.IconContainer}>
          <FilterIcon setFilterVisible={setFilterVisible} FilterVisible={FilterVisible} />
        </View>
        <View style={CommonStyles.IconContainer}>
          {EnableExport() === true
            ? <ExportIcon handleExportClick={handleExportClick} newData={newData} />
            : null
          }
        </View>
        <SelectedFilters selectedMonth={selectedMonth} selectedPackage={selectedPackage} selectedYear={selectedYear} />
      </View>
      <Modal
        animationType="fade"
        transparent={true}
        visible={FilterVisible}
        onRequestClose={() => { setFilterVisible(!FilterVisible); }}>
        <View style={formCustomStyles.centeredView}>
          <FilterBox
            FilterVisible={FilterVisible}
            setFilterVisible={setFilterVisible}
            screenWidth={screenWidth}
            CommonStyles={CommonStyles}
            yearFilterOptions={yearFilterOptions}
            handleYearFilterSelect={handleYearFilterSelect}
            selectedYear={selectedYear}
            monthFilterOptions={monthFilterOptions}
            handleMonthFilterSelect={handleMonthFilterSelect}
            selectedMonth={selectedMonth}
            packageFilterOptions={packageFilterOptions}
            handlePackageFilterSelect={handlePackageFilterSelect}
            selectedPackage={selectedPackage}
            handleFilterReset={handleFilterReset}
            setSelectedMonth={setSelectedMonth}
            setSelectedYear={setSelectedYear}
            setSelectedPackage={setSelectedPackage}
            setNewData={setNewData}
            updatedData={updatedData}
          />
        </View>
        <View>
          <ToastMessage
            show={toastVisibility}
            actionLabel="Dismiss"
            toastMessage={toastText}
            duration={3000}
            onDismiss={handleToastDismiss}
          />
        </View>
      </Modal>
      <View style={logStyles.logRoot}>
        {isDashboard ? (
          <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
            <View style={{ zIndex: 200 }}>
              <LogKpiRow items={kpiItems}>
                <LogDateRangeFilter data={newData} onFilterChange={setDateRangeFiltered} />
              </LogKpiRow>
            </View>
            <View>
              <View style={[CommonStyles.container, CommonStyles.divider]}>
                {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                <View key={1} style={[CommonStyles.card, { backgroundColor: '' }]}>
                  <PieChartComp data={chartData.bysoilTypePieData} colorPallate={interpolateBlues} subTitle={'(Cumulative)'} ChartTitle={"Soil Qty (Tonnes) / Type"} />
                </View>
                {/* LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string */}
                <View key={2} style={[CommonStyles.card, { backgroundColor: '' }]}>
                  <LineChartScreen legend={chartData.legendData1} data={chartData.lineData} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"Soil Qty (Tonnes) / Type"} />
                </View>
                <View key={3} style={[CommonStyles.card, { backgroundColor: '' }]}>
                  <PieChartComp data={chartData.byUsePieData} colorPallate={interpolateGnBu} subTitle={'(Cumulative)'} ChartTitle={"Soil Qty (Tonnes) / Use"} />
                </View>
                <View key={4} style={[CommonStyles.card, { backgroundColor: '' }]}>
                  <LineChartScreen legend={chartData.legendData2} data={chartData.lineData2} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"Soil Qty (Tonnes) / Use"} />
                </View>
              </View>
            </View>
          </ScrollView>
        ) : (
          <TabsProvider defaultIndex={0}>
            <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
              <TabScreen label="Charts" icon="chart-bar">
                <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                  <View style={{ zIndex: 200 }}>
                    <LogKpiRow items={kpiItems}>
                      <LogDateRangeFilter data={newData} onFilterChange={setDateRangeFiltered} />
                    </LogKpiRow>
                  </View>
                  <View>
                    <View style={[CommonStyles.container, CommonStyles.divider]}>
                      {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                      <View key={1} style={[CommonStyles.card, { backgroundColor: '' }]}>
                        <PieChartComp data={chartData.bysoilTypePieData} colorPallate={interpolateBlues} subTitle={'(Cumulative)'} ChartTitle={"Soil Qty (Tonnes) / Type"} />
                      </View>
                      {/* LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string */}
                      <View key={2} style={[CommonStyles.card, { backgroundColor: '' }]}>
                        <LineChartScreen legend={chartData.legendData1} data={chartData.lineData} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"Soil Qty (Tonnes) / Type"} />
                      </View>
                      <View key={3} style={[CommonStyles.card, { backgroundColor: '' }]}>
                        <PieChartComp data={chartData.byUsePieData} colorPallate={interpolateGnBu} subTitle={'(Cumulative)'} ChartTitle={"Soil Qty (Tonnes) / Use"} />
                      </View>
                      <View key={4} style={[CommonStyles.card, { backgroundColor: '' }]}>
                        <LineChartScreen legend={chartData.legendData2} data={chartData.lineData2} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"Soil Qty (Tonnes) / Use"} />
                      </View>
                    </View>
                  </View>
                </ScrollView>
              </TabScreen>
              <TabScreen label="List" icon="database">
                <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                  <View>
                    <View style={CommonStyles.container}>
                      <View style={CommonStyles.cardThree}>
                        {dateRangeFiltered && dateRangeFiltered.length > 0 && ColumnsWithIDColumn && (
                          <PaperTableDynamic
                            data={dateRangeFiltered}
                            columns={ColumnsWithIDColumn}
                            siteId={siteId}
                            logTitle={logTitle}
                            updateData={() => {}}
                            isConsolidatedTable={true}
                          />
                        )}
                      </View>
                    </View>
                  </View>
                </ScrollView>
              </TabScreen>
            </Tabs>
          </TabsProvider>
        )}
      </View>
    </View>
  );
};
